import { tracked } from '@glimmer/tracking';

import { BlockVisualization, DataSelectType } from 'webapp/enums/enums';
import { hashCode } from 'webapp/utils/number';

import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import type DChartPointBase from 'webapp/d-chart/point/point-base';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import DChartSeries from 'webapp/d-chart/series/series';

const RecordLimit = 15;

export default class VisualizationProgress extends VisualizationBase {
	visualizationType = BlockVisualization.PROGRESS;

	recordLimit = RecordLimit;

	// ------- visualization - data --------

	@tracked series: DChartSeries[] = [];

	get output() {
		return this.blockSetup.firstShownDataSelect?.dataRoot?.outputs[0] || null;
	}

	get outputs() {
		const outputs: DsDataOutputTopLevel[] = [];
		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			outputs.push(...ds.outputs);
		});
		return outputs;
	}

	get isInverted() {
		return !!this.output?.dataRoot.invert;
	}

	get periodEndTS() {
		return this.output?.dataRoot.periodEndTS;
	}

	override updateDataCache() {
		this.series = this.getSeries();
	}

	override clearDataCache() {
		this.series = [];
	}

	protected getSeries() {
		const series: DChartSeries[] = [];

		this.outputs.forEach((output) => {
			const { minPoint, primaryPoint, maxPoint } = this.createPointsForOutput(output);
			if (primaryPoint) {
				const serie = new DChartSeries({
					type: DataSelectType.MAIN,
					timezone: this.dataTimezone,
					title: output.shortTitle,
					points: [minPoint!, primaryPoint, maxPoint!],
					granulationLabels: output.dataRoot.granulationLabels,
					metricSelectionData: output.metricSelectionData,
				});
				series.push(serie);
			}
		});

		return series;
	}

	protected createPointsForOutput(output: DsDataOutputTopLevel | null): {
		minPoint: DChartPrimaryPoint | null;
		primaryPoint: DChartPrimaryPoint | null;
		maxPoint: DChartPrimaryPoint | null;
	} {
		let primaryPoint: DChartPrimaryPoint | null = null,
			minPoint: DChartPrimaryPoint | null = null,
			maxPoint: DChartPrimaryPoint | null = null;

		if (output) {
			const points = this.createDChartPointsFromOutput(output);

			primaryPoint = points[0] || null;

			if (primaryPoint) {
				const startValue = output.dataRoot.dataSelect.startValue || 0;
				// create min point
				minPoint = new DChartPrimaryPoint({
					value: startValue,
					format: primaryPoint.format,
					scale: primaryPoint.scale,
					dateRange: primaryPoint.dateRange,
					goalData: primaryPoint.goalData,
				});

				// create PrimaryPoint for max value
				let maxPointValue = this.getComparePointByCompareIndex(
					this.indexForMaxValueCompare,
					primaryPoint,
				)?.value;
				const isAutoMaxPoint = typeof maxPointValue !== 'number';
				if (isAutoMaxPoint) {
					// automatic max: value + 20%
					maxPointValue = this.getAutoMaxPointValue(primaryPoint, startValue);
				}
				maxPoint = new DChartPrimaryPoint({
					value: maxPointValue,
					format: primaryPoint.format,
					scale: primaryPoint.scale,
					loading: isAutoMaxPoint, // will use this flag to hide actual compare change
					dateRange: primaryPoint.dateRange,
					goalData: primaryPoint.goalData,
				});
			}
		}

		return {
			minPoint,
			primaryPoint,
			maxPoint,
		};
	}

	protected getAutoMaxPointValue(point: DChartPointBase, startValue: number): number {
		const max = Math.max(point.value || 0, startValue);
		if (max === startValue && max === 0) {
			// start at 0, all the rest are either 0 or negatives
			// in this case we set max to 1 for gauge to look presentable
			return 1;
		}
		return max < 0 ? max * 0.8 : max * 1.2;
	}

	protected getComparePointByCompareIndex(
		compareIndex: number,
		primaryPoint: DChartPrimaryPoint | null = null,
	) {
		return (
			(primaryPoint?.comparePoints || []).find((cp) => cp.compareIndex === compareIndex) ||
			null
		);
	}

	// ------- visualization specifics --------

	indexForMaxValueCompare = 0;

	// ------- visualization switch helpers --------

	override preCleanupForSwitchTo(visualizationTo: AnyVisualization): void {
		super.preCleanupForSwitchTo(visualizationTo);

		// clear compares when switching away from Progress-based (gauge, radial prog)
		if (!visualizationTo.showMaxValue) {
			this.loggerService.logToConsole(
				'[Vis change] from Gauge/Progresses: Removing all compares',
			);
			this.blockSetup.primaryDataSelects.forEach((ds) => {
				ds.removeCompares(true);
			});
		}

		if (!visualizationTo.showMinValue) {
			this.loggerService.logToConsole(
				'[Vis change] from Gauge/Progresses: Removing startValue',
			);
			this.blockSetup.primaryDataSelects.forEach((ds) => {
				ds.startValue = null;
			});
		}

		if (
			visualizationTo.visualizationType !== BlockVisualization.GAUGE &&
			visualizationTo.visualizationType !== BlockVisualization.PROGRESS
		) {
			// cleanup progress-specifics
			this.loggerService.logToConsole(
				'[Vis change] from Progress: cleanup showTrafficLights, hideTimeLeft',
			);
			this.block.showTrafficLights = null;
			this.block.hideTimeLeft = null;
		}
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const dss = this.blockSetup.dataSelects;
		return hashCode(
			JSON.stringify({
				stl: this.block.showTrafficLights,
				htl: this.block.hideTimeLeft,
				o: dss.map((ds) => ds.order),
				sv: dss.map((ds) => ds.startValue),
			}),
		);
	}

	static override recordLimit = RecordLimit;
}
